import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    getMainView,
    ISettingsPageContext,
    SettingsCardMeta,
    SettingsPageContext,
    SettingsRowMeta,
    SettingsSectionMeta,
    SettingsPendingChange,
} from "./SettingsPageContext";

type Props = {
    editedKeys: string[];
    children: React.ReactNode;
};

/** Extra space between the sticky header and the element a jump lands on. */
const JUMP_GAP = 12;

/** How long a row stays highlighted after a jump from search or the unsaved-changes list. */
const HIGHLIGHT_MS = 2200;

type ElementFinder = (id: string) => HTMLElement | null;

/**
 * Registration order follows mount order, which follows render order, but a section that
 * re-mounts (a module turned on) would land at the end. Sort by DOM position instead.
 */
const sortByDocumentOrder = <T extends { id: string }>(items: T[], findElement: ElementFinder): T[] => {
    if (typeof document === "undefined") {
        return items;
    }

    return [...items].sort((a, b) => {
        const elA = findElement(a.id);
        const elB = findElement(b.id);

        if (!elA || !elB || elA === elB) {
            return 0;
        }

        return elA.compareDocumentPosition(elB) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });
};

const replaceById = <T extends { id: string }>(items: T[], meta: T): T[] => [...items.filter((item) => item.id !== meta.id), meta];

/**
 * Owns the registry of sections, cards and rows on the page, the scroll-spy that tracks which of
 * them sits under the sticky header, and the jump helpers. State that belongs to the supplier
 * model (edited keys, save, discard) stays in SupplierProfileOptions and comes in as props.
 */
export const SettingsPageProvider = (props: Props) => {
    /**
     * Every id lookup is scoped to the page. The app's side navigation gives its links ids like
     * "orders" and "inventory" (SideNavLink.tsx), and it sits before the main view in the DOM,
     * so a document-wide getElementById would measure the sidebar instead of the section.
     */
    const rootRef = useRef<HTMLDivElement>(null);

    const findElement = useCallback<ElementFinder>((id: string) => {
        const root = rootRef.current;

        if (!root) {
            return null;
        }

        return root.querySelector<HTMLElement>(`[id="${id}"]`);
    }, []);

    const [sectionsRaw, setSectionsRaw] = useState<SettingsSectionMeta[]>([]);
    const [cardsRaw, setCardsRaw] = useState<SettingsCardMeta[]>([]);
    const [rowsRaw, setRowsRaw] = useState<SettingsRowMeta[]>([]);

    const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
    const [activeCardId, setActiveCardId] = useState<string | null>(null);
    const [highlightedRowId, setHighlightedRowId] = useState<string | null>(null);
    const [headerHeight, setHeaderHeight] = useState(0);
    const highlightTimer = useRef<number | null>(null);

    const registerSection = useCallback((meta: SettingsSectionMeta) => {
        setSectionsRaw((items) => replaceById(items, meta));

        return () => {
            setSectionsRaw((items) => items.filter((item) => item.id !== meta.id));
        };
    }, []);

    const registerCard = useCallback((meta: SettingsCardMeta) => {
        setCardsRaw((items) => replaceById(items, meta));

        return () => {
            setCardsRaw((items) => items.filter((item) => item.id !== meta.id));
        };
    }, []);

    const registerRow = useCallback((meta: SettingsRowMeta) => {
        setRowsRaw((items) => replaceById(items, meta));

        return () => {
            setRowsRaw((items) => items.filter((item) => item.id !== meta.id));
        };
    }, []);

    const sections = useMemo(() => sortByDocumentOrder(sectionsRaw, findElement), [sectionsRaw, findElement]);
    const cards = useMemo(() => sortByDocumentOrder(cardsRaw, findElement), [cardsRaw, findElement]);
    const rows = useMemo(() => sortByDocumentOrder(rowsRaw, findElement), [rowsRaw, findElement]);

    const jumpTo = useCallback(
        (elementId: string) => {
            const mainView = getMainView();
            const element = findElement(elementId);

            if (!mainView || !element) {
                return;
            }

            const mainRect = mainView.getBoundingClientRect();
            const rect = element.getBoundingClientRect();
            const top = mainView.scrollTop + (rect.top - mainRect.top) - headerHeight - JUMP_GAP;

            mainView.scrollTo({ top: Math.max(0, top), behavior: "auto" });
        },
        [headerHeight, findElement]
    );

    const jumpToRow = useCallback(
        (rowId: string) => {
            jumpTo(rowId);
            setHighlightedRowId(rowId);

            if (highlightTimer.current) {
                window.clearTimeout(highlightTimer.current);
            }

            highlightTimer.current = window.setTimeout(() => {
                setHighlightedRowId(null);
            }, HIGHLIGHT_MS);
        },
        [jumpTo]
    );

    useEffect(() => {
        return () => {
            if (highlightTimer.current) {
                window.clearTimeout(highlightTimer.current);
            }
        };
    }, []);

    /**
     * Scroll-spy: the active section / card is the last one whose top has scrolled past the
     * bottom edge of the sticky header. Runs on the main view's scroll, throttled to a frame.
     */
    useEffect(() => {
        const mainView = getMainView();

        if (!mainView) {
            return;
        }

        let frame: number | null = null;

        const update = () => {
            frame = null;

            const mainRect = mainView.getBoundingClientRect();
            const line = mainRect.top + headerHeight + JUMP_GAP + 1;

            const lastAbove = <T extends { id: string }>(items: T[]): T | null => {
                let found: T | null = null;

                for (const item of items) {
                    const element = findElement(item.id);

                    if (!element) {
                        continue;
                    }

                    if (element.getBoundingClientRect().top <= line) {
                        found = item;
                    } else {
                        break;
                    }
                }

                return found;
            };

            const section = lastAbove(sections) || sections[0] || null;
            const cardsInSection = cards.filter((card) => card.sectionId === (section ? section.id : null));
            const card = lastAbove(cardsInSection) || cardsInSection[0] || null;

            setActiveSectionId(section ? section.id : null);
            setActiveCardId(card ? card.id : null);
        };

        const onScroll = () => {
            if (frame === null) {
                frame = window.requestAnimationFrame(update);
            }
        };

        update();
        mainView.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);

        return () => {
            mainView.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);

            if (frame !== null) {
                window.cancelAnimationFrame(frame);
            }
        };
    }, [sections, cards, headerHeight, findElement]);

    const editedKeys = props.editedKeys;

    const isEdited = useCallback((key: string) => editedKeys.includes(key), [editedKeys]);

    const [pending, setPending] = useState<SettingsPendingChange[]>([]);

    const registerPending = useCallback((change: SettingsPendingChange) => {
        setPending((items) => [...items.filter((item) => item.key !== change.key), change]);

        return () => {
            setPending((items) => items.filter((item) => item.key !== change.key));
        };
    }, []);

    const value: ISettingsPageContext = useMemo(
        () => ({
            sections,
            cards,
            rows,
            registerSection,
            registerCard,
            registerRow,
            activeSectionId,
            activeCardId,
            jumpTo,
            jumpToRow,
            highlightedRowId,
            editedKeys,
            isEdited,
            headerHeight,
            setHeaderHeight,
            pending,
            registerPending,
        }),
        [
            sections,
            cards,
            rows,
            registerSection,
            registerCard,
            registerRow,
            activeSectionId,
            activeCardId,
            jumpTo,
            jumpToRow,
            highlightedRowId,
            editedKeys,
            isEdited,
            headerHeight,
            pending,
            registerPending,
        ]
    );

    return (
        <SettingsPageContext.Provider value={value}>
            <div ref={rootRef} className={"SettingsPage-root"}>
                {props.children}
            </div>
        </SettingsPageContext.Provider>
    );
};

export default SettingsPageProvider;
